/**
 * ExercisePickerModal.jsx — Sélecteur d'exercices plein écran pour le créateur de programme
 * débutant "Choisir moi-même" : recherche + filtres (matériel, niveau) sur la grande
 * bibliothèque Body-Up (~450 exercices, photos, GET /api/exercises), avec badge "Recommandé"
 * calculé selon le niveau déclaré par le client.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Loader2, Check, Plus, Sparkles, ImageOff } from 'lucide-react';
import { useClientAuth } from '../../../context/ClientAuthContext';

const LEVEL_SLUG = { 'Débutant': 'debutant', 'Intermédiaire': 'intermediaire', 'Avancé': 'avance' };
const LEVEL_RANK = { debutant: 0, intermediaire: 1, avance: 2, pro: 3 };
const stripAccents = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const ExercisePickerModal = ({
  en, muscleGroup, equipmentFilter = [], clientLevel, alreadySelectedIds = new Set(),
  onAdd, onClose,
}) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [search, setSearch] = useState('');
  const [equipTab, setEquipTab] = useState(equipmentFilter[0] || 'all');

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/exercises?muscle_group=${encodeURIComponent(muscleGroup)}&limit=300`)
      .then((r) => r.json())
      .then((d) => setExercises(d.exercises || []))
      .catch(() => setExercises([]))
      .finally(() => setLoading(false));
  }, [authFetch, muscleGroup]);

  const clientLevelSlug = LEVEL_SLUG[clientLevel] || 'debutant';
  const equipTabs = useMemo(() => ['all', ...equipmentFilter], [equipmentFilter]);

  const filtered = useMemo(() => {
    const q = stripAccents(search.trim());
    let list = exercises.filter((e) => (equipTab === 'all' || e.equipment === equipTab));
    if (q) list = list.filter((e) => stripAccents(e.name_fr).includes(q));
    return list.slice().sort((a, b) => {
      const aRec = LEVEL_RANK[a.level] <= LEVEL_RANK[clientLevelSlug] ? 1 : 0;
      const bRec = LEVEL_RANK[b.level] <= LEVEL_RANK[clientLevelSlug] ? 1 : 0;
      if (aRec !== bRec) return bRec - aRec;
      return (a.name_fr || '').localeCompare(b.name_fr || '');
    });
  }, [exercises, equipTab, search, clientLevelSlug]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="exercise-picker-modal">
      <div className="w-full sm:max-w-lg sm:rounded-3xl bg-zinc-900 border border-white/10 rounded-t-3xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-white/10 shrink-0">
          <h3 className="font-black text-white text-base">{muscleGroup}</h3>
          <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-white" data-testid="exercise-picker-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-3 space-y-2.5 border-b border-white/10 shrink-0">
          <div className="relative">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={L('Rechercher un exercice...', 'Search an exercise...')}
              className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm placeholder:text-zinc-600" data-testid="exercise-picker-search-input" />
          </div>
          {equipTabs.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
              {equipTabs.map((eq) => (
                <button key={eq} onClick={() => setEquipTab(eq)} data-testid={`exercise-picker-equip-tab-${eq}`}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${equipTab === eq ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200' : 'bg-white/[0.04] border-white/10 text-zinc-400'}`}>
                  {eq === 'all' ? L('Tout', 'All') : eq}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-emerald-400" /></div>}
          {!loading && filtered.length === 0 && (
            <p className="text-center text-sm text-zinc-500 py-10">{L('Aucun exercice trouvé.', 'No exercise found.')}</p>
          )}
          {filtered.map((ex) => {
            const already = alreadySelectedIds.has(ex.id);
            const recommended = LEVEL_RANK[ex.level] <= LEVEL_RANK[clientLevelSlug];
            return (
              <button key={ex.id} onClick={() => !already && onAdd(ex)} disabled={already}
                className={`w-full flex items-center gap-3 rounded-xl border p-2.5 text-left transition-colors ${already ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:border-emerald-500/30'}`}
                data-testid={`exercise-picker-item-${ex.id}`}>
                <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden shrink-0 flex items-center justify-center">
                  {ex.image_url ? <img src={ex.image_url} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-4 h-4 text-zinc-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{en ? (ex.name_en || ex.name_fr) : ex.name_fr}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {recommended && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        <Sparkles className="w-2.5 h-2.5" /> {L('Recommandé', 'Recommended')}
                      </span>
                    )}
                    <span className="text-[10px] text-zinc-500">{ex.equipment}</span>
                  </div>
                </div>
                {already ? <Check className="w-5 h-5 text-emerald-400 shrink-0" /> : <Plus className="w-5 h-5 text-zinc-500 shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExercisePickerModal;
